import { useState, useEffect } from 'react';
import { Plus, Inbox, Zap, AlertTriangle, Edit3, CheckCircle2, ClipboardList, Clock, CircleCheckBig } from 'lucide-react';
import TicketCard from './TicketCard';
import NewTicketModal from './NewTicketModal';
import BatchScanModal from './BatchScanModal';

const COLUMNS = [
  { id: 'backlog',             label: 'Backlog',             icon: Inbox,         color: '#71717a', accent: 'rgba(113,113,122,0.15)', desc: 'Not yet analyzed' },
  { id: 'analyzing',           label: 'Analyzing',           icon: Zap,           color: '#6366f1', accent: 'rgba(99,102,241,0.15)',  desc: 'AI agents processing' },
  { id: 'needs_clarification', label: 'Needs Clarification', icon: AlertTriangle, color: '#f59e0b', accent: 'rgba(245,158,11,0.15)', desc: 'Ambiguity score > 50' },
  { id: 'refined',             label: 'Refined',             icon: Edit3,         color: '#06b6d4', accent: 'rgba(6,182,212,0.15)',   desc: 'Rewrite accepted' },
  { id: 'ready_for_dev',       label: 'Ready for Dev',       icon: CheckCircle2,  color: '#10b981', accent: 'rgba(16,185,129,0.15)',  desc: 'Clear and actionable' },
  { id: 'done',                label: 'Done',                icon: CircleCheckBig, color: '#22d3ee', accent: 'rgba(34,211,238,0.15)', desc: 'Completed' },
];

const RISK_COLOR = { critical: '#dc2626', high: '#ef4444', medium: '#f59e0b', low: '#10b981' };

function RateLimitOverlay({ resetTime }) {
  const [remaining, setRemaining] = useState('');

  useEffect(() => {
    const tick = () => {
      const diff = Math.max(0, Math.ceil((resetTime - Date.now()) / 1000));
      if (diff <= 0) { setRemaining(''); return; }
      const m = Math.floor(diff / 60);
      const s = diff % 60;
      setRemaining(`${m}:${s.toString().padStart(2, '0')}`);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [resetTime]);

  if (!remaining) return null;

  return (
    <div style={{
      position: 'absolute', inset: 0, zIndex: 20,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'rgba(9,9,11,0.88)', backdropFilter: 'blur(6px)',
    }}>
      <div style={{ textAlign: 'center' }}>
        <Clock size={48} color="#6366f1" style={{ marginBottom: 16 }} />
        <div style={{ color: '#f4f4f5', fontSize: 20, fontWeight: 700, marginBottom: 8 }}>
          Rate Limit Reached
        </div>
        <div style={{ color: '#a1a1aa', fontSize: 14, marginBottom: 20 }}>
          10 scans per hour. You can scan again in:
        </div>
        <div style={{
          color: '#6366f1', fontSize: 48, fontWeight: 800, letterSpacing: '-0.03em',
          fontFamily: 'JetBrains Mono, monospace',
        }}>
          {remaining}
        </div>
      </div>
    </div>
  );
}

export default function KanbanBoard({ tickets, isAnalyzing, agentProgress, failedAnalysis = {}, batchProgress, rateLimitReset, onAnalyze, onCardClick, onMove, onNew, onDelete, onBatchScan }) {
  const [showModal, setShowModal] = useState(false);
  const [showBatchModal, setShowBatchModal] = useState(false);
  const [draggedId, setDraggedId] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);

  const colTickets = (colId) => tickets.filter(t => t.column === colId);
  const isEmpty = tickets.length === 0;

  const handleDragStart = (e, ticketId) => { setDraggedId(ticketId); e.dataTransfer.effectAllowed = 'move'; };
  const handleDragOver = (e, colId) => { e.preventDefault(); setDropTarget(colId); };
  const handleDrop = (e, colId) => {
    e.preventDefault();
    if (draggedId) {
      const t = tickets.find(t => t.id === draggedId);
      if (t && t.column !== colId && !isAnalyzing[draggedId]) onMove(draggedId, colId);
    }
    setDraggedId(null); setDropTarget(null);
  };

  const batchPct = batchProgress
    ? Math.round((batchProgress.completed / batchProgress.total) * 100)
    : 0;

  const showRateLimit = rateLimitReset && rateLimitReset > Date.now();

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', position: 'relative' }}>
      {showRateLimit && <RateLimitOverlay resetTime={rateLimitReset} />}

      {/* Toolbar */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '14px 24px 10px', flexShrink: 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ color: '#e4e4e7', fontSize: 15, fontWeight: 700, letterSpacing: '-0.01em' }}>
            Board
          </span>
          <span style={{
            color: '#71717a', fontSize: 12, fontWeight: 600,
            background: 'rgba(255,255,255,0.04)', borderRadius: 6, padding: '2px 8px',
          }}>
            {tickets.length} ticket{tickets.length !== 1 ? 's' : ''}
          </span>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            data-testid="batch-scan-btn"
            onClick={() => setShowBatchModal(true)}
            disabled={!!batchProgress}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              background: 'rgba(255,255,255,0.03)',
              border: '1px solid rgba(255,255,255,0.08)',
              borderRadius: 8, padding: '8px 14px', color: '#a1a1aa',
              fontSize: 13, fontWeight: 600, cursor: batchProgress ? 'not-allowed' : 'pointer',
              fontFamily: 'inherit', transition: 'all 0.15s',
              opacity: batchProgress ? 0.5 : 1,
            }}
            onMouseOver={e => { if (!batchProgress) { e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.12)'; } }}
            onMouseOut={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.03)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)'; }}
          >
            <ClipboardList size={14} />
            Batch Scan
          </button>
          <button
            data-testid="new-ticket-btn"
            onClick={() => setShowModal(true)}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
              border: 'none', borderRadius: 8,
              padding: '8px 18px', color: '#fff', fontSize: 13, fontWeight: 600,
              cursor: 'pointer', fontFamily: 'inherit',
              boxShadow: '0 2px 12px rgba(99,102,241,0.35)', transition: 'all 0.15s',
            }}
            onMouseOver={e => { e.currentTarget.style.boxShadow = '0 4px 20px rgba(99,102,241,0.5)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
            onMouseOut={e => { e.currentTarget.style.boxShadow = '0 2px 12px rgba(99,102,241,0.35)'; e.currentTarget.style.transform = 'translateY(0)'; }}
          >
            <Plus size={15} />
            New Ticket
          </button>
        </div>
      </div>

      {/* Batch progress */}
      {batchProgress && (
        <div
          data-testid="batch-progress-bar"
          style={{
            margin: '0 24px 10px',
            background: 'rgba(99,102,241,0.04)', border: '1px solid rgba(99,102,241,0.15)',
            borderRadius: 10, padding: '10px 16px',
            display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0,
          }}
        >
          <span style={{ color: '#818cf8', fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap', flexShrink: 0 }}>Batch Scan:</span>
          <span style={{ color: '#a1a1aa', fontSize: 12, whiteSpace: 'nowrap', flexShrink: 0 }}>Analyzing {batchProgress.current} of {batchProgress.total}...</span>
          <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
            {Object.entries(batchProgress.counts).map(([level, count]) =>
              count > 0 ? (
                <span key={level} style={{
                  fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 6,
                  background: `${RISK_COLOR[level]}12`, color: RISK_COLOR[level],
                  border: `1px solid ${RISK_COLOR[level]}30`, textTransform: 'capitalize',
                }}>{count} {level}</span>
              ) : null
            )}
          </div>
          <div style={{ flex: 1, background: 'rgba(255,255,255,0.06)', borderRadius: 4, height: 6, overflow: 'hidden', minWidth: 80 }}>
            <div style={{ width: `${batchPct}%`, height: '100%', borderRadius: 4, transition: 'width 0.5s ease-out' }} className="batch-progress-fill" />
          </div>
          <span style={{ color: '#818cf8', fontSize: 12, fontWeight: 700, flexShrink: 0, minWidth: 32, textAlign: 'right' }}>{batchPct}%</span>
        </div>
      )}

      {/* Columns */}
      <div style={{ flex: 1, display: 'flex', overflowX: 'auto', overflowY: 'hidden', gap: 0, padding: '0 12px' }} className="scrollbar-hide">
        {isEmpty && !batchProgress && (
          <div style={{ position: 'absolute', left: '50%', top: '55%', transform: 'translate(-50%, -50%)', textAlign: 'center', pointerEvents: 'none' }}>
            <div style={{
              width: 64, height: 64, borderRadius: 16, background: 'rgba(99,102,241,0.08)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 16px', border: '1px solid rgba(99,102,241,0.15)',
            }}>
              <ClipboardList size={28} color="#6366f1" />
            </div>
            <div style={{ color: '#71717a', fontSize: 16, fontWeight: 600, marginBottom: 6 }}>No tickets yet</div>
            <div style={{ color: '#3f3f46', fontSize: 13 }}>Create a ticket, try a Use Case, or Batch Scan</div>
          </div>
        )}

        {COLUMNS.map((col, i) => {
          const colItems = colTickets(col.id);
          const Icon = col.icon;
          const isActive = dropTarget === col.id;

          return (
            <div
              key={col.id}
              data-testid={`kanban-col-${col.id}`}
              style={{
                flex: '1 1 0', minWidth: 260, maxWidth: 360,
                display: 'flex', flexDirection: 'column',
                margin: '0 4px',
                borderRadius: 12,
                background: isActive ? 'rgba(99,102,241,0.04)' : 'rgba(255,255,255,0.015)',
                border: isActive ? '1px dashed rgba(99,102,241,0.3)' : '1px solid rgba(255,255,255,0.04)',
                transition: 'all 0.2s',
                overflow: 'hidden',
              }}
              onDragOver={e => handleDragOver(e, col.id)}
              onDrop={e => handleDrop(e, col.id)}
              onDragLeave={() => setDropTarget(null)}
            >
              {/* Column header */}
              <div style={{
                padding: '14px 16px 12px',
                borderBottom: `2px solid ${col.color}`,
                background: 'rgba(0,0,0,0.2)',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{
                      width: 26, height: 26, borderRadius: 7,
                      background: col.accent,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <Icon size={14} color={col.color} />
                    </div>
                    <span style={{ color: '#e4e4e7', fontSize: 13, fontWeight: 700, letterSpacing: '-0.01em' }}>
                      {col.label}
                    </span>
                  </div>
                  <span style={{
                    background: colItems.length > 0 ? col.accent : 'rgba(255,255,255,0.04)',
                    color: colItems.length > 0 ? col.color : '#52525b',
                    fontSize: 11, fontWeight: 700, borderRadius: 6, padding: '2px 8px',
                    minWidth: 22, textAlign: 'center',
                  }}>
                    {colItems.length}
                  </span>
                </div>
                <div style={{ fontSize: 10, color: '#52525b', letterSpacing: '0.02em', paddingLeft: 34 }}>{col.desc}</div>
              </div>

              {/* Cards */}
              <div style={{ flex: 1, overflowY: 'auto', padding: '8px 8px 12px' }} className="scrollbar-hide">
                {colItems.map(ticket => (
                  <TicketCard
                    key={ticket.id}
                    ticket={ticket}
                    isAnalyzing={!!isAnalyzing[ticket.id]}
                    agentProgress={agentProgress[ticket.id]}
                    hasFailed={!!failedAnalysis[ticket.id]}
                    onClick={() => onCardClick(ticket)}
                    onAnalyze={() => onAnalyze(ticket.id, ticket.text, ticket.title)}
                    onDelete={() => onDelete(ticket.id)}
                    onDragStart={e => handleDragStart(e, ticket.id)}
                    onDragEnd={() => { setDraggedId(null); setDropTarget(null); }}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {showModal && <NewTicketModal onClose={() => setShowModal(false)} onCreate={onNew} />}
      {showBatchModal && <BatchScanModal onClose={() => setShowBatchModal(false)} onBatchScan={onBatchScan} />}
    </div>
  );
}
